/**
 * Single source of truth for suburb × service SEO landing pages.
 *
 * To add a new suburb:
 *   1. Add an entry under SUBURBS with unique local hook copy + nearby list.
 *   2. Run `node scripts/build-sitemap.js` from /app/frontend (or just commit
 *      — CI/deploy regenerates it). 3 new pages ship automatically.
 */

export const SERVICES = {
  ielts: {
    key: 'ielts',
    slug: 'ielts-tutor',
    name: 'IELTS Tutoring',
    label: 'IELTS',
    shortDesc: 'IELTS coaching for Academic, General Training, band-7+ candidates.',
    // Service-level unique copy (merged into each page, but the combination
    // with suburb data guarantees each final page differs materially).
    why: [
      'Certified IELTS trainers with proven band 7+ track records',
      'Full-skill coverage: Listening, Reading, Writing, Speaking',
      'Authentic full-length mock tests marked against the official band descriptors',
      'Weekly writing task feedback within 48 hours',
      'Flexible online or in-person sessions — fits around work and study',
    ],
    whatYouGet: [
      'Initial diagnostic test to identify your current band level',
      'Personalised 8–12 week study plan tailored to your target band',
      'One-on-one weekly sessions (60–90 minutes)',
      'Marked practice essays with line-by-line feedback',
      'Speaking-module rehearsals with recorded feedback',
      'Unlimited email support between sessions',
    ],
    whoFor: [
      'University applicants targeting band 7.0, 7.5 or 8.0',
      'Skilled migration candidates (PR points)',
      'Health professionals sitting IELTS for AHPRA registration',
      'International students preparing for Australian universities',
    ],
    keywordBase: 'IELTS tutor',
    ctaHeadline: 'Book Your Free IELTS Diagnostic',
  },

  vce: {
    key: 'vce',
    slug: 'vce-tutors',
    name: 'VCE Tutoring',
    label: 'VCE',
    shortDesc: 'Expert VCE tutoring across English, Maths Methods, Specialist, Biology, Chemistry, Physics and more.',
    why: [
      'Tutors who themselves scored 40+ raw in the subjects they teach',
      'Full VCAA past-paper library and exam-technique training',
      'SAC preparation aligned with each school\u2019s assessment style',
      'Bespoke study notes and summary booklets',
      'Regular practice exams under timed conditions',
    ],
    whatYouGet: [
      'Detailed gap analysis of your current SAC results',
      'Subject-specific weekly sessions with a specialist tutor',
      'Access to curated VCE resource library (notes, flashcards, past SACs)',
      'Mock exams with full examiner-style feedback',
      'ATAR-maximising subject-selection advice',
      'Parent progress reports every 4 weeks',
    ],
    whoFor: [
      'Year 11 & Year 12 students aiming for ATAR 90+',
      'Students who have fallen behind in a specific subject',
      'High-achievers pushing from a raw 35 into the 45+ range',
      'International students navigating VCE for the first time',
    ],
    keywordBase: 'VCE tutor',
    ctaHeadline: 'Start Your VCE Improvement Plan',
  },

  maths: {
    key: 'maths',
    slug: 'maths-tutor',
    name: 'Maths Tutoring',
    label: 'Maths',
    shortDesc: 'Primary, secondary and senior Maths tutoring from foundations to Specialist Maths.',
    why: [
      'Tutors covering every level from Year 3 through to VCE Specialist',
      'Diagnostic-led teaching — find the exact gap, then close it',
      'Worked examples rewritten for each student\u2019s learning style',
      'Weekly concept review to lock in long-term retention',
      'Regular topic tests modelled on your child\u2019s school assessments',
    ],
    whatYouGet: [
      'Free initial skills assessment across key Maths strands',
      'Customised weekly lesson plan targeting weak areas',
      '1-on-1 tutoring sessions (45–60 minutes depending on year level)',
      'Homework help and exam-prep boosters',
      'Progress tracker shared with parents',
      'Practice question banks matched to the Victorian curriculum',
    ],
    whoFor: [
      'Primary students building number-sense and times-tables confidence',
      'Year 7–10 students preparing for NAPLAN and Selective Entry',
      'VCE Maths Methods and Specialist Maths candidates',
      'Students needing targeted help before a SAC or exam',
    ],
    keywordBase: 'Maths tutor',
    ctaHeadline: 'Book a Free Maths Assessment',
  },
};

/**
 * Suburb registry — each suburb's local hook is unique so the combined
 * page content is substantively different across the network.
 */
export const SUBURBS = {
  melbourne: {
    slug: 'melbourne',
    name: 'Melbourne',
    postcode: '3000',
    hook:
      'As the capital of Victoria and the education hub of Australia, Melbourne is home to some of the nation\u2019s most competitive schools and universities. Students from the CBD and inner suburbs routinely push themselves against an incredibly high academic bar, which is why having the right tutor can be the difference between a solid result and a standout one.',
    nearby: ['Carlton', 'Fitzroy', 'Southbank', 'Docklands', 'North Melbourne', 'Richmond'],
    highlight: 'home to the University of Melbourne, RMIT and numerous top-ranking selective schools',
  },
  richmond: {
    slug: 'richmond',
    name: 'Richmond',
    postcode: '3121',
    hook:
      'Richmond sits at the heart of inner-eastern Melbourne — a neighbourhood known as much for Swan Street and the MCG as for its tight-knit community of ambitious families. Richmond parents consistently look for tutors who can match the pace of high-performing Catholic and independent schools in the area, and who can commute conveniently across the inner east.',
    nearby: ['South Yarra', 'East Melbourne', 'Abbotsford', 'Cremorne', 'Collingwood'],
    highlight: 'close to Melbourne Girls\u2019 College, Melbourne High School, and leading Catholic colleges',
  },
  'south-yarra': {
    slug: 'south-yarra',
    name: 'South Yarra',
    postcode: '3141',
    hook:
      'South Yarra combines premium lifestyle with academic intensity. Families along Toorak Road and Chapel Street expect tutors who can deliver results to match the rigour of the area\u2019s private and independent schools. With easy tram and train access, South Yarra is also a natural hub for students commuting from Toorak, Prahran and Armadale for weekly lessons.',
    nearby: ['Toorak', 'Prahran', 'Windsor', 'Armadale', 'Richmond'],
    highlight: 'a short tram ride from leading independent schools across Melbourne\u2019s south-east',
  },
  'box-hill': {
    slug: 'box-hill',
    name: 'Box Hill',
    postcode: '3128',
    hook:
      'Box Hill has become one of Melbourne\u2019s most academically competitive corridors. With families gathered around Box Hill Central and the Whitehorse Road strip, there\u2019s enormous demand for tutoring that can push students from solid to outstanding — particularly in Selective Entry preparation, VCE science subjects, and advanced Maths.',
    nearby: ['Surrey Hills', 'Blackburn', 'Mont Albert', 'Balwyn', 'Doncaster'],
    highlight: 'a short drive from Box Hill High School, Koonung Secondary College and Balwyn High School',
  },
  'glen-waverley': {
    slug: 'glen-waverley',
    name: 'Glen Waverley',
    postcode: '3150',
    hook:
      'Glen Waverley is one of Victoria\u2019s highest-performing school catchments. Families along Kingsway and The Glen shopping precinct routinely benchmark their children against top-10 school rankings, which means the tutoring bar is unusually high. Local students thrive when they have a specialist who understands both the curriculum and the local school culture.',
    nearby: ['Mount Waverley', 'Wheelers Hill', 'Vermont South', 'Mulgrave', 'Notting Hill'],
    highlight: 'home to Glen Waverley Secondary College, Mount Waverley Secondary, and surrounding high-performing independent schools',
  },
};

/**
 * Generate every { service, suburb } combination as a flat array — used by
 * route registration and sitemap generation.
 */
export const SEO_PAGES = Object.values(SERVICES).flatMap((service) =>
  Object.values(SUBURBS).map((suburb) => ({
    service,
    suburb,
    path: `/${service.slug}-${suburb.slug}`,
    title: `${service.name} in ${suburb.name} | Winhouse Tutors`,
    description: buildDescription(service, suburb),
  }))
);

function buildDescription(service, suburb) {
  const desc = `Expert ${service.name.toLowerCase()} in ${suburb.name} ${suburb.postcode}. ${service.shortDesc} Online or in-person across ${suburb.name} and nearby ${suburb.nearby[0]}.`;
  // Ensure ≤ 160 chars for search-snippet friendliness
  return desc.length > 160 ? `${desc.slice(0, 157).trim()}...` : desc;
}

export const getPageByPath = (path) =>
  SEO_PAGES.find((p) => p.path === path) || null;
